<script lang="ts" setup>
// Родной компонент DataTable слишком косячный.
// Данный компонент призван убрать проблемы с рендером

import { ref, computed, watch } from "vue"
import type { TableColumn } from "@/components/alerts/types"
import get from "lodash/get"
import Paginator from "primevue/paginator"
import Button from "primevue/button"

const props = defineProps<{
  size: "small" | "medium" | "large"
  totalRecords?: number
  rows?: number
  fakeRowsCount?: number
  lazy?: boolean
  paginator?: boolean
  loading?: boolean
  formattingRules?: Record<string, (v: string, ctx?: Record<string, unknown>, field_name?: string) => string>
  skeletonHeight?: string
  value?: unknown[]
  colFull?: boolean
  columns: TableColumn[]
  groupBy: string
  groupSortField?: string
  groupSortOrder?: number
}>()

const formattingRules = computed(() => {
  return props.formattingRules ?? {}
})

const collapsedAll = ref(true)
const uncollapseClass = "text-center collapser-class"
const shownGroups = ref<Set<string>>(new Set())
const sortField = defineModel<string>('sortField')
const sortOrder = defineModel<number>('sortOrder')
const groups = ref<[string, ItemsGroup][]>([])

const value = computed(() => {
  if (!props.lazy && sortField.value) {
    const arr = props.value?.slice() as any[] // eslint-disable-line
    arr.sort((a, b) => {
      if (sortField.value) {
        if (sortOrder.value == -1) {
          return (a?.[sortField.value] ?? 1) > (b?.[sortField.value] ?? 1) ? 1 : -1
        } else {
          return (a?.[sortField.value] ?? 1) < (b?.[sortField.value] ?? 1) ? 1 : -1
        }
      }
      return 1
    })
    return arr

  } else {
    return props.value as any[] // eslint-disable-line
  }
})

const showSkeletons = computed(() => {
  const data = value.value
  return (data?.length ?? 0) == 0
})

// Размер текста
const textSizeClasses = {
  'small': 'text-sm',
  'medium': 'text-md',
  'large': 'text-lg',
}
const textSizeClass = computed(() => textSizeClasses[props.size])
const currentPage = ref<number>(0)

const page = (page_num: number) => {
  if (props.loading || page_num == currentPage.value) {
    return
  }
  emit('page', page_num, sortField.value ?? null, sortOrder.value ?? null)
  currentPage.value = page_num
}

const emit = defineEmits<{
  (e: "page", page: number, field: string | null, order: number | null): void
  (e: "sort"): void
}>()

const callRule = (field: string, data: Record<string, unknown>) => {
  if (!formattingRules.value || !data || !field) { return }
  const fieldValue = get(data, field) as string
  const rules = formattingRules.value
  if (field in rules) {
    return rules[field]?.(fieldValue, data, field)
  }
  return fieldValue
}

const onSort = (fieldName: string) => {
  if (sortField.value == fieldName) {
    sortOrder.value = sortOrder.value == 1 ? -1 : 1
  } else {
    sortField.value = fieldName
    sortOrder.value = -1
  }
  emit('sort')
}

// const getClasses = (classes: string | null | undefined) => {
//   if (!classes) {
//     classes = ''
//   }
//   return classes
// }

interface ItemsGroup {
  header: any | null      // eslint-disable-line
  nested: any[]    // eslint-disable-line
}

watch([() => props.value, () => props.groupSortField, () => props.groupSortOrder], ([v]) => {
  const map = new Map<string, ItemsGroup>()
  const entries: [string, ItemsGroup][] = []
  v?.forEach((item) => {
    const groupName = String(get(item, props.groupBy))
    const existing = map.get(groupName)
    if (existing) {
      existing.nested.push(item)
    } else {
      const group: ItemsGroup = { header: item, nested: [] }
      map.set(groupName, group)
      entries.push([groupName, group])
    }
  })

  const sortField = props.groupSortField ?? ''
  entries.sort(([, a], [, b]) => (get(a.header, sortField) ?? 0) - (get(b.header, sortField) ?? 0))
  if (props.groupSortOrder == -1) {
    entries.reverse()
  }
  groups.value = entries
}, { immediate: true })

const genClass = (col: TableColumn, forceNp = false) => {
  const noPadding = (col.noPadding ?? false) || forceNp
  return [
    { "sr-table-body-column-content": !noPadding },
    col.class,
    textSizeClass.value
  ]
}
</script>

<template>
  <div class="sr-table-wrapper">
    <table class="sr-table table-fixed" :class="{ 'table-loading': props.loading }">
      <thead class="sr-table-thead">
        <!-- Оглавление -->
        <tr>
          <td style="width: 40px" :class="uncollapseClass">
            <Button size="small" v-if="collapsedAll" icon="pi pi-plus-circle" rounded variant="text"
              @click="collapsedAll = false">
            </Button>
            <Button v-else size="small" icon="pi pi-minus-circle" rounded variant="text" @click="collapsedAll = true">
            </Button>
          </td>
          <th class="sr-table-header-cell sr-table-header-column-content" v-for="col in props.columns" :key="col.field"
            :class="genClass(col, true)" :style="col.width ? { width: col.width } : undefined">
            <div :class="{ 'cursor-pointer hover': col.sortable }"
              @click="col.sortable ? onSort(col.field) : undefined">
              <span class="sr-table-header-column-title">
                {{ col.header ? $t(col.header) : '' }}
              </span>
              <span v-if="col.sortable">
                <i class="ms-2 pi" style="font-size: 10px;"
                  :class="{ 'pi-sort-up-fill': sortField == col.field && sortOrder == -1, 'pi-sort-down-fill': !(sortField == col.field && sortOrder == -1) }"></i>
              </span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="st-table-tbody">

        <!-- Скелетоны -->
        <!-- <template v-if="loading && Object.keys(groups).length == 0">
          <tr v-for="idx in [...new Array(props.rows).keys()]" :key="idx">
            <td>
              <div :style="`height: ${props.skeletonHeight}`" class="skeleton-bar"></div>
            </td>
            <td class="sr-table-body-column-content" :class="`${textSizeClass} ${col.class}`"
              v-for="col in props.columns" :key="col.field">
              <div :style="`height: ${props.skeletonHeight}`" class="skeleton-bar"></div>
            </td>
          </tr>
        </template> -->

        <!-- Скелетоны -->
        <template v-if="loading && showSkeletons">
          <tr v-for="idx in [...new Array(props.fakeRowsCount ?? 15).keys()]" :key="idx">
            <td style="width: 40px">
              <div :style="`height: ${props.skeletonHeight};width: 32px`" class="skeleton-bar max-w-full"></div>
            </td>
            <td :class="genClass(col)" v-for="col in props.columns" :key="col.field"
              :style="col.width ? { width: col.width } : undefined">
              <div :style="`height: ${props.skeletonHeight};width: ${(col.skeletonWidth ?? '100px')}`"
                class="skeleton-bar max-w-full"></div>
            </td>
          </tr>
        </template>

        <!-- Группы -->

        <!-- Пустая таблица -->
        <tr v-if="value?.length == 0 && !loading">
          <td class="sr-table-body-column-content" :colspan="props.columns.length + 1">
            <slot name="empty"></slot>
          </td>
        </tr>

        <template v-for="[key, data] in groups" :key="key">

          <!-- Оглавление -->
          <tr class="header">
            <td class="text-center" style="width: 40px">
              <template v-if="data.nested.length > 0 && collapsedAll">
                <Button v-if="!shownGroups.has(key)" icon="pi pi-plus-circle" rounded variant="text" size="small"
                  @click="shownGroups.add(key)">

                </Button>
                <Button v-else size="small" icon="pi pi-minus-circle" rounded variant="text"
                  @click="shownGroups.delete(key)">

                </Button>
              </template>
            </td>
            <td v-for="col in props.columns" :key="col.field" :class="genClass(col)"
              :style="col.width ? { width: col.width } : undefined">
              <component
                v-bind="col.dynamic && col.innerPropsGenerator && data.header ? col.innerPropsGenerator(data.header) : col.innerProps"
                :is="col.inner ?? 'span'">
                {{ col.dynamic && col.innerDataGenerator && data.header ? col.innerDataGenerator(data.header) :
                  callRule(col.field,
                    data.header) }}
              </component>
            </td>
          </tr>

          <!-- Вложенные элементы -->
          <template v-for="nestedRow in data.nested" :key="nestedRow.id">
            <tr v-if="shownGroups.has(key) || !collapsedAll" class="nested">
              <template v-if="data">
                <td style="width: 40px"></td>
                <td v-for="col in props.columns" :key="col.field" :class="genClass(col)"
                  :style="col.width ? { width: col.width } : undefined">
                  <component
                    v-bind="col.dynamic && col.innerPropsGenerator && nestedRow ? col.innerPropsGenerator(nestedRow) : col.innerProps"
                    :is="col.inner ?? 'span'">
                    {{ col.dynamic && col.innerDataGenerator && nestedRow ? col.innerDataGenerator(nestedRow) :
                      callRule(col.field,
                        nestedRow) }}
                  </component>
                </td>
              </template>
              <template v-else>
                <td></td>
              </template>
            </tr>
          </template>

        </template>

      </tbody>
    </table>
  </div>
  <hr class="table-end" />
  <Paginator v-if="props.paginator ?? false" :rows="props.rows" @page="page($event.page)"
    :totalRecords="props.totalRecords ? props.totalRecords : props.rows">
  </Paginator>
</template>
